import React, { useContext, useMemo, useRef, useState } from 'react';
import { func } from 'prop-types';
import { connect } from 'react-redux';
import { Box, Flex } from 'rebass';
import { ButtonStyled, TextStyled, useTokens } from '@nike/nike-design-system-components';
import { CSVLink } from 'react-csv';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import TableServerSide from '../../components/tableServerSide/TableServerSide';
import { ActionItemList } from '../common/ActionItemList';
import LineItemsModal from './lineItemsModal/LineItemsModal';

import { showToast } from '../../components/toast/actions';
import useModal from '../../hooks/useModal';
import { getOrderIssueColumns } from './orderIssueColumns';
import { handleResubmitOrders, handleFetchAllTroubledOrders, getOrderLineItems } from './actions';

const formatOrderForCSV = order => {
  if (!order) {
    return {};
  }

  return {
    'Account Name': order.accountName,
    'Account Number': order.accountNumber,
    'Cart Id': order.cartId,
    'Create Timestamp': order.createTimestamp,
    'Department Name': order.departmentName,
    'Error Message': (order.errorMessage || '').replace(/,/g, ''),
    'OFOA ID': order.ofoaId,
    'Order ID': order.orderId,
    'Order Status': order.orderStatus,
    'Order Type': order.orderType,
    'Purchase Method': order.purchaseMethod,
    'Purchase Order Number': order.purchaseOrderNumber
  };
};

const OrderIssuesList = ({ showToast }) => {
  const tokens = useTokens();
  const csvLinkRef = useRef(null);

  const [allTroubledOrders, setAllTroubledOrders] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();
  const [isLineItemsModalOpen, openLineItemsModal, closeLineItemsModal] = useModal();

  const handleOpenLineItemsModal = async orderId => {
    openLineItemsModal();
    setIsLoading(true);
    setSelectedOrderId(orderId);
    const lineItems = await getOrderLineItems(orderId);
    // should sort line items by productCode so on refresh they look the same
    setLineItems(lineItems);
    setIsLoading(false);
  };

  const handleCloseLineItemsModal = () => {
    closeLineItemsModal();
    setLineItems([]);
    setSelectedOrderId(null);
  };

  const refreshLineItems = async orderId => {
    setIsLoading(true);
    const lineItems = await getOrderLineItems(orderId);
    setLineItems(lineItems);
    setIsLoading(false);
  };

  const memoizedColumns = useMemo(() => getOrderIssueColumns({ handleOpenLineItemsModal }), []);

  const {
    totalResources,
    handleClearSortAndFilters,
    sortQueryParams,
    filterQueryParams,
    tableInstance: { selectedRowIds = {}, isAllRowsSelected, selectedFlatRows }
  } = useContext(TableServerSideContext);

  const selectedRows = selectedFlatRows?.map(row => row.original);

  const csvLinkData = (isAllRowsSelected ? allTroubledOrders : selectedRows)?.map(order => formatOrderForCSV(order));

  const actionItems = {
    'Resubmit Order': {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Submit',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Re-submitting orders...');

                try {
                  let troubledOrders = selectedRows;

                  if (isAllRowsSelected) {
                    troubledOrders = await handleFetchAllTroubledOrders({
                      totalResources,
                      sortQueryParams,
                      filterQueryParams
                    });
                  }

                  const orderIds = troubledOrders?.map(row => row.orderId).filter(Boolean) || [];

                  await handleResubmitOrders(orderIds);

                  showToast({ message: 'Orders re-submitted', type: 'success' });
                } catch (error) {
                  console.error('Error re-submitting orders:', error);
                  showToast({
                    message: 'Failed to re-submit orders. Please try again.',
                    type: 'error',
                    error
                  });
                } finally {
                  toggleIsLoading(false);
                  closeActionItemModal();
                }
              }
          }
        ],
        content: () => 'Do you want to proceed with re-submitting the selected orders?',
        title: 'Confirm Submit'
      }
    },
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Downloading CSV...');

                try {
                  if (isAllRowsSelected) {
                    const allTroubledOrders = await handleFetchAllTroubledOrders({
                      totalResources,
                      sortQueryParams,
                      filterQueryParams
                    });

                    setAllTroubledOrders(allTroubledOrders);
                  }

                  setTimeout(() => csvLinkRef?.current?.link.click(), 1000);
                  showToast({ message: 'Orders downloaded', type: 'success' });
                } catch (error) {
                  console.error('Error downloading orders:', error);
                  showToast({
                    message: 'Failed to download orders. Please try again.',
                    type: 'error',
                    error
                  });
                } finally {
                  toggleIsLoading(false);
                  closeActionItemModal();
                }
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected orders?',
        title: 'Confirm Download'
      }
    }
  };

  return (
    <Box style={{ backgroundColor: tokens.paletteGrey5 }}>
      <Flex
        sx={{
          borderBottom: `1px solid ${tokens.paletteGrey3}`,
          padding: '12px',
          width: '100%'
        }}
      >
        <TextStyled appearance='title2'>Order Issues</TextStyled>
      </Flex>
      <hr />
      <Flex p='12px' width='100%'>
        <Flex alignItems='center' width='50%'>
          <CSVLink data={csvLinkData || []} filename='TroubledOrders.csv' ref={csvLinkRef} target='_blank' />

          <TextStyled appearance='body3' style={{ marginLeft: '5px' }}>
            {`${totalResources}`} results
          </TextStyled>

          <ButtonStyled
            appearance='text'
            background='light'
            ariaLabel='Clear All Filters'
            type='button'
            onClick={handleClearSortAndFilters}
            style={{ marginLeft: '20px', marginRight: '20px' }}
          >
            Clear All Filters
          </ButtonStyled>

          <ActionItemList
            selected={selectedRowIds}
            actionItems={actionItems}
            isModalOpen={isActionItemModalOpen}
            openModal={openActionItemModal}
            closeModal={closeActionItemModal}
          />
        </Flex>
      </Flex>

      <Box px='12px'>
        <TableServerSide
          columns={memoizedColumns}
          url='v1/admin/troubledOrders'
          includeRowSelect
          fullHeight
          fullWidth
        />
      </Box>
      {isLineItemsModalOpen && (
        <LineItemsModal
          closeModal={handleCloseLineItemsModal}
          isOpen={isLineItemsModalOpen}
          orderId={selectedOrderId}
          lineItems={lineItems}
          lineItemsLoading={isLoading}
          refreshLineItems={refreshLineItems}
        />
      )}
    </Box>
  );
};

OrderIssuesList.propTypes = {
  showToast: func.isRequired
};

export default connect(null, { showToast })(OrderIssuesList);
